import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { sendAdminTestEmail } from "@/lib/email/test.functions";
import { checkStripeHealth, type StripeHealthResult } from "@/lib/health/stripe-health.functions";

export const Route = createFileRoute("/admin/settings")({ component: AdminSettings });

function AdminSettings() {
  const [rows, setRows] = useState<Record<string, string>>({});
  const [sendingTest, setSendingTest] = useState(false);
  const [stripeHealth, setStripeHealth] = useState<StripeHealthResult | null>(null);
  const [checkingStripe, setCheckingStripe] = useState(false);

  async function load() {
    const { data } = await supabase.from("site_settings").select("*");
    const m: Record<string, string> = {};
    for (const r of data ?? []) m[r.key] = JSON.stringify(r.value, null, 2);
    setRows(m);
  }
  useEffect(() => { load(); }, []);

  async function save(key: string) {
    try {
      const value = JSON.parse(rows[key]);
      const { error } = await supabase.from("site_settings").upsert({ key, value });
      if (error) throw error;
      toast.success(`${key} guardado`);
    } catch (e: any) { toast.error(e.message); }
  }

  async function handleSendTest() {
    setSendingTest(true);
    try {
      const sess = await supabase.auth.getSession();
      const token = sess.data.session?.access_token;
      if (!token) throw new Error("Sesión expirada");
      const res = await sendAdminTestEmail({
        headers: { Authorization: `Bearer ${token}` },
      } as any);
      toast.success(`Email de prueba enviado a ${res.to}`);
    } catch (e: any) {
      toast.error(`No se pudo enviar: ${e?.message ?? "error"}`);
    } finally {
      setSendingTest(false);
    }
  }

  async function handleStripeHealth() {
    setCheckingStripe(true);
    try {
      const sess = await supabase.auth.getSession();
      const token = sess.data.session?.access_token;
      if (!token) throw new Error("Sesión expirada");
      const res = await checkStripeHealth({
        headers: { Authorization: `Bearer ${token}` },
      } as any);
      setStripeHealth(res);
      if (res.ok) toast.success("Stripe operativo");
      else toast.error(`Stripe con problemas (${res.errors.length})`);
    } catch (e: any) {
      toast.error(`Health check falló: ${e?.message ?? "error"}`);
    } finally {
      setCheckingStripe(false);
    }
  }

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-foreground">Site settings</h2>
      <p className="mt-2 text-sm text-muted-foreground">Edita el JSON de cada bloque (hero, contact, legal, features, brand).</p>

      <div className="mt-4 rounded-md border border-hairline bg-surface p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-label-accent">Email transaccional</Label>
            <p className="mt-1 text-sm text-muted-foreground">
              Envía un email de prueba a la dirección pública de la marca para verificar que Resend está operativo.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleSendTest}
            disabled={sendingTest}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
          >
            {sendingTest ? "Enviando…" : "Enviar email de prueba"}
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-hairline bg-surface p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-label-accent">Stripe health check</Label>
            <p className="mt-1 text-sm text-muted-foreground">
              Verifica que la clave secreta esté presente en runtime, que la API de Stripe responde, y que el webhook endpoint está activo y validando firmas.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleStripeHealth}
            disabled={checkingStripe}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
          >
            {checkingStripe ? "Verificando…" : "Verificar Stripe"}
          </Button>
        </div>
        {stripeHealth && (
          <div className="mt-3 rounded border border-hairline bg-surface-2 p-3 text-xs font-mono">
            <div className={stripeHealth.ok ? "text-green-500" : "text-destructive"}>
              {stripeHealth.ok ? "✓ Operativo" : "✗ Con problemas"} · {new Date(stripeHealth.checked_at).toLocaleString()}
            </div>
            <ul className="mt-2 space-y-0.5">
              <li>{stripeHealth.checks.secret_key_present ? "✓" : "✗"} STRIPE_SECRET_KEY presente</li>
              <li>{stripeHealth.checks.webhook_secret_present ? "✓" : "✗"} STRIPE_WEBHOOK_SECRET presente</li>
              <li>{stripeHealth.checks.site_url_present ? "✓" : "✗"} SITE_URL presente</li>
              <li>{stripeHealth.checks.stripe_api_reachable ? "✓" : "✗"} API Stripe responde {stripeHealth.checks.stripe_account_country ? `(país: ${stripeHealth.checks.stripe_account_country})` : ""} {stripeHealth.checks.stripe_livemode === true ? "[LIVE]" : stripeHealth.checks.stripe_livemode === false ? "[TEST]" : ""}</li>
              <li>{stripeHealth.checks.webhook_endpoint_reachable ? "✓" : "✗"} Webhook endpoint activo (HTTP {stripeHealth.checks.webhook_endpoint_status ?? "n/a"}, esperado 400)</li>
              {stripeHealth.checks.webhook_endpoint_url && (
                <li className="break-all opacity-70">URL: {stripeHealth.checks.webhook_endpoint_url}</li>
              )}
            </ul>
            {stripeHealth.errors.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-destructive">
                {stripeHealth.errors.map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-4">
        {Object.entries(rows).map(([k, v]) => (
          <div key={k} className="rounded-md border border-hairline bg-surface p-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-label-accent">{k}</Label>
              <Button size="sm" onClick={() => save(k)} className="bg-primary text-primary-foreground hover:bg-primary/90">Guardar</Button>
            </div>
            <Textarea rows={Math.min(20, v.split("\n").length + 1)} className="mt-3 bg-surface-2 border-hairline font-mono text-xs" value={v} onChange={(e) => setRows((p) => ({ ...p, [k]: e.target.value }))} />
          </div>
        ))}
      </div>
    </div>
  );
}
